// The attention model of CHT-730 (the seam) and CHT-734 (the rest): which handoffs a staff member must not miss, how
// they are ordered, which events a later notification consumer (Expo push, Phase 12) reads, and the timings that
// operational reporting uses. Everything here is computed from TIMESTAMPS, CODES and IDS: never from message text
// (contract sections 4.5 and 8.2). Thresholds are CONFIGURATION: no launch SLA is invented, so a threshold that is
// not configured switches its rule off.
/**
 * A customer who writes while their handoff waits. `conversations.last_customer_activity_at`
 * moves forward with every customer message (lane A's webhook projection, from the Stream send time) and with the
 * request itself. The request writes both in one transaction, so at the instant of the request the two
 * times are equal; only a later customer message makes the activity strictly newer. Only a
 * WAITING handoff can be updated while waiting: an ACCEPTED one has an owner who sees the thread.
 */
export function isCustomerUpdatedWhileWaiting(input) {
    return (input.status === "WAITING" &&
        input.lastCustomerActivityAt !== null &&
        input.lastCustomerActivityAt.getTime() > input.requestedAt.getTime());
}
export const NO_ATTENTION_THRESHOLDS = Object.freeze({
    staleAfterMs: null,
    escalateAfterMs: null,
});
/** The longest threshold that is accepted: a week. A longer wait is not an operational signal. */
export const MAX_THRESHOLD_MINUTES = 7 * 24 * 60;
function minutes(value) {
    if (value === undefined || value.trim() === "")
        return null;
    const text = value.trim();
    if (!/^\d{1,5}$/.test(text))
        return "invalid";
    const parsed = Number(text);
    return parsed >= 1 && parsed <= MAX_THRESHOLD_MINUTES ? parsed * 60_000 : "invalid";
}
/**
 * Reads the two thresholds from an environment-like record. Unset or empty is off. A value that is not a whole
 * number of minutes from 1 to 10,080 is refused by NAME (never its value), so a typo cannot silently turn the rule
 * on at a wrong length or off.
 */
export function parseAttentionThresholds(env) {
    const escalate = minutes(env.LAFAYETTE_HANDOFF_ESCALATE_AFTER_MINUTES);
    if (escalate === "invalid")
        return { ok: false, variable: "LAFAYETTE_HANDOFF_ESCALATE_AFTER_MINUTES" };
    const stale = minutes(env.LAFAYETTE_HANDOFF_STALE_AFTER_MINUTES);
    if (stale === "invalid")
        return { ok: false, variable: "LAFAYETTE_HANDOFF_STALE_AFTER_MINUTES" };
    return {
        ok: true,
        thresholds: Object.freeze({ staleAfterMs: stale, escalateAfterMs: escalate }),
    };
}
// --- attention codes and order ------------------------------------------------------------------------------------------
/** The badges of a handoff in the staff inbox. Repeats `staffAttentionCodeValues` of `@lafayette/schemas` (parity-tested). */
export const staffAttentionCodes = Object.freeze([
    "NEW_REQUEST",
    "WAITING",
    "CUSTOMER_UPDATED_WHILE_WAITING",
    "STALE",
    "FALLBACK_ROUTED",
    "UNASSIGNED_COVERAGE",
]);
/**
 * The badges of a handoff, most urgent first. Only a WAITING handoff has any. `NEW_REQUEST` is structural (the
 * handoff was requested and routed and nothing happened since: version 2, no customer update), so it needs no
 * threshold. `STALE` needs `staleAfterMs`. `FALLBACK_ROUTED` is a backup, the supervisor queue, or a handoff that was
 * escalated. `UNASSIGNED_COVERAGE` is the explicit exception: nobody can take it.
 */
export function attentionCodes(input) {
    if (input.status !== "WAITING")
        return [];
    const codes = [];
    const updated = isCustomerUpdatedWhileWaiting({
        status: input.status,
        requestedAt: input.requestedAt,
        lastCustomerActivityAt: input.lastCustomerActivityAt,
    });
    if (input.targetKind === "UNASSIGNED")
        codes.push("UNASSIGNED_COVERAGE");
    if (updated)
        codes.push("CUSTOMER_UPDATED_WHILE_WAITING");
    if (input.thresholds.staleAfterMs !== null &&
        input.now.getTime() - input.requestedAt.getTime() >= input.thresholds.staleAfterMs) {
        codes.push("STALE");
    }
    if (input.targetKind === "BACKUP" ||
        input.targetKind === "SUPERVISOR_QUEUE" ||
        input.escalationCount > 0) {
        codes.push("FALLBACK_ROUTED");
    }
    if (input.version === 2 && !updated && input.escalationCount === 0)
        codes.push("NEW_REQUEST");
    codes.push("WAITING");
    return codes;
}
/**
 * The order of the inbox, as a number: lower is more urgent. An explicit coverage gap first, then a customer who
 * wrote while waiting, then a stale handoff, then a fallback, then a new request, then any other waiting handoff,
 * then everything that is not waiting. The SQL of the inbox repeats these tiers in one expression; a database test
 * proves the two agree on a matrix.
 */
export const ATTENTION_TIER_OTHER = 9;
export function attentionTier(codes) {
    if (codes.includes("UNASSIGNED_COVERAGE"))
        return 0;
    if (codes.includes("CUSTOMER_UPDATED_WHILE_WAITING"))
        return 1;
    if (codes.includes("STALE"))
        return 2;
    if (codes.includes("FALLBACK_ROUTED"))
        return 3;
    if (codes.includes("NEW_REQUEST"))
        return 4;
    if (codes.includes("WAITING"))
        return 5;
    return ATTENTION_TIER_OTHER;
}
// --- the reusable attention event ---------------------------------------------------------------------------------------
/**
 * The domain event a notification consumer reads (contract section 8.2). It is the SAME shape for every handoff
 * state change, so a later push consumer subscribes to it and the handoff state is not redesigned. This phase
 * writes it to `attention_events` (codes and ids only) and shows it in the staff inbox; no provider code exists.
 */
export const attentionEventKinds = Object.freeze([
    "HANDOFF_REQUESTED",
    "HANDOFF_ROUTED",
    "HANDOFF_ESCALATED",
    "CUSTOMER_UPDATED_WHILE_WAITING",
    "HANDOFF_ACCEPTED",
    "HANDOFF_RELEASED",
    "COVERAGE_GAP",
    // CHT-732: the server removed a staff message of a person who does not own the accepted handoff (the before-send guard failed open).
    // It is not a state change of the handoff: it belongs to the CURRENT version and tells the owner, so one version has at most one.
    "STAFF_MESSAGE_REMOVED",
]);
/**
 * The attention events of one handoff transition. The same transaction writes them (`attention_events`), so there
 * is no event without its state change and no state change without its event. A route to `UNASSIGNED` adds the
 * explicit `COVERAGE_GAP` event. A transfer, a re-target and an escalation tell the NEW holder.
 */
export function attentionEventsForTransition(input) {
    const base = {
        conversationId: input.conversationId,
        customerId: input.customerId,
        handoffId: input.handoffId,
        handoffVersion: input.handoffVersion,
    };
    const event = (kind, targetStaffUserId, code) => Object.freeze({ ...base, kind, targetStaffUserId, code });
    const gap = () => input.targetKind === "UNASSIGNED" ? [event("COVERAGE_GAP", null, "UNASSIGNED")] : [];
    switch (input.action) {
        case "handoff.requested":
        case "handoff.requested_by_staff":
            return [event("HANDOFF_REQUESTED", null, input.reasonCode)];
        case "handoff.routed":
            return [event("HANDOFF_ROUTED", input.targetStaffUserId, input.targetKind), ...gap()];
        case "handoff.reassigned":
            return [event("HANDOFF_ROUTED", input.targetStaffUserId, "REASSIGNED")];
        case "handoff.escalated":
            return [event("HANDOFF_ESCALATED", input.targetStaffUserId, input.reasonCode), ...gap()];
        case "handoff.accepted":
            return [event("HANDOFF_ACCEPTED", input.ownerStaffUserId, "ACCEPTED")];
        case "handoff.owner_transferred":
            return [event("HANDOFF_ACCEPTED", input.ownerStaffUserId, "OWNER_TRANSFERRED")];
        case "handoff.released":
            return [event("HANDOFF_RELEASED", input.previousHolderStaffUserId, "RELEASED")];
        case "handoff.cancelled":
            return [event("HANDOFF_RELEASED", input.previousHolderStaffUserId, "CANCELLED")];
    }
}
/** The event for a customer who wrote while the handoff waits: it belongs to the CURRENT version and tells the current target. */
export function customerUpdatedEvent(input) {
    return Object.freeze({
        kind: "CUSTOMER_UPDATED_WHILE_WAITING",
        conversationId: input.conversationId,
        customerId: input.customerId,
        handoffId: input.handoffId,
        handoffVersion: input.handoffVersion,
        targetStaffUserId: input.targetStaffUserId,
        code: null,
    });
}
/**
 * The event for a staff message that the server removed (CHT-732): somebody who does not own the handoff wrote into the customer
 * channel. It belongs to the CURRENT version of the handoff of the conversation and tells its OWNER (or nobody in particular when the
 * handoff has none yet: the supervisors read the exception). `code` is the closed refusal code of `decideStaffMessage` and never text.
 * `(handoff, kind, version)` is unique, so several removals under one version write one event; each removal has its own audit row.
 */
export function staffMessageRemovedEvent(input) {
    return Object.freeze({
        kind: "STAFF_MESSAGE_REMOVED",
        conversationId: input.conversationId,
        customerId: input.customerId,
        handoffId: input.handoffId,
        handoffVersion: input.handoffVersion,
        targetStaffUserId: input.ownerStaffUserId,
        code: input.code,
    });
}
const gap = (from, to) => from === null || to === null ? null : Math.max(0, to.getTime() - from.getTime());
export function handoffTimings(input) {
    return Object.freeze({
        timeToRouteMs: gap(input.requestedAt, input.routedAt),
        timeToAcceptMs: gap(input.requestedAt, input.acceptedAt),
        firstHumanResponseMs: gap(input.acceptedAt, input.firstStaffResponseAt),
        requestToFirstHumanResponseMs: gap(input.requestedAt, input.firstStaffResponseAt),
        timeToResolveMs: gap(input.requestedAt, input.resolvedAt),
    });
}
